import { Box, Typography, Button } from "@mui/material";
import { OpenInNew } from "@mui/icons-material";

/**
 * FeatureCard - Reusable card with optional icon or gold accent title pill.
 * Used in BeliefsSection (homepage), AssessmentSection (academics), and FounderSection (about).
 *
 * @param {string} title - Card title
 * @param {string} description - Card description text (alternative to children)
 * @param {string|React.ReactNode} children - Card body content
 * @param {React.ElementType} icon - Optional MUI icon component to display above title
 * @param {boolean} iconBackground - Show icon in gold circle background (default: false)
 * @param {boolean} centerAlign - Center body text alignment (default: true)
 * @param {boolean} pillTitle - Show title in gold pill style (default: true, false when icon is used)
 * @param {string} action - Optional action button text
 * @param {string} actionHref - Optional action button link
 * @param {boolean} actionExternal - If true, opens link in new tab
 */
export default function FeatureCard({ 
  title, 
  description,
  children, 
  icon: IconComponent, 
  iconBackground = false,
  centerAlign = true,
  pillTitle = true,
  action,
  actionHref,
  actionOnClick,
  actionExternal = false,
}) {
  // If an icon is provided, default to no pill style
  const showPill = pillTitle && !IconComponent;
  const textAlign = centerAlign ? "center" : "left";
  const hasAction = action && (actionHref || actionOnClick);

  return (
    <Box
      sx={{
        p: 4,
        backgroundColor: "background.paper",
        borderRadius: "24px",
        border: "1px solid",
        borderColor: "divider",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "all 0.2s ease",
        // Only apply hover effect when there's no action button
        ...(!hasAction && {
          "&:hover": {
            borderColor: "primary.main",
            transform: "translateY(-4px)",
            boxShadow: "0 8px 24px rgba(60, 86, 59, 0.12)",
          },
        }),
      }}
    >
      {/* Icon (if provided) */}
      {IconComponent && (
        <Box sx={{ mb: 2, textAlign }}>
          {iconBackground ? (
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                backgroundColor: "background.accent",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: "primary.main",
              }}
            >
              <IconComponent sx={{ fontSize: "2rem" }} />
            </Box>
          ) : (
            <Box sx={{ color: "primary.main" }}>
              <IconComponent sx={{ fontSize: "2rem" }} />
            </Box>
          )}
        </Box>
      )}

      {/* Title - pill style or plain */}
      {showPill ? (
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <Box
            sx={{
              display: "inline-block",
              backgroundColor: "background.accent",
              px: 2.5,
              py: 0.75,
              borderRadius: "24px",
            }}
          >
            <Typography
              variant="h3"
              component="h3"
              sx={{
                color: "primary.main",
                fontWeight: 700,
                fontSize: "1rem",
              }}
            >
              {title}
            </Typography>
          </Box>
        </Box>
      ) : (
        <Typography
          variant="h4"
          component="h3"
          sx={{
            fontSize: "1.25rem",
            fontWeight: 700,
            color: "text.primary",
            mb: 2,
            textAlign,
          }}
        >
          {title}
        </Typography>
      )}

      {/* Card body */}
      <Box sx={{ textAlign, flexGrow: 1 }}>
        <Typography component="div" sx={{ color: "text.primary", lineHeight: 1.7, fontSize: "1rem" }}>
          {description || children}
        </Typography>
      </Box>

      {/* Optional Action Button */}
      {action && (actionHref || actionOnClick) && (
        <Box sx={{ textAlign: "center", mt: 3 }}>
          <Button
            variant="outlined"
            href={actionOnClick ? undefined : actionHref}
            onClick={actionOnClick}
            target={actionExternal ? "_blank" : undefined}
            rel={actionExternal ? "noopener noreferrer" : undefined}
            sx={{
              borderColor: "primary.main",
              color: "primary.main",
              fontWeight: 600,
              borderRadius: "2rem",
              px: 3,
              py: 1,
              "&:hover": {
                backgroundColor: "primary.main",
                color: "primary.contrastText",
                borderColor: "primary.main",
              },
            }}
            endIcon={actionExternal ? <OpenInNew sx={{ fontSize: "1rem" }} /> : undefined}
          >
            {action}
          </Button>
        </Box>
      )}
    </Box>
  );
}

